import { createTranslation } from '@choffmeister/i18next'
import { css, SerializedStyles } from '@emotion/core'
import parse from 'html-react-parser'
import * as React from 'react'

import { breakpoints } from '../constants/breakPoints'
import { Testimonial, testimonials } from '../constants/testimonials'
import { isDesktop, isMobile } from '../helpers/common'
import Block from './Block'
import Image from './Image'
import { Link } from './Link'
import PaginationBlock from './PaginationBlock'

interface TestimonialCardProps {
  bgColor?: 'blue' | 'green' | 'purple' | 'peach'
  testimonialData?: Testimonial[]
  cssStyles?: SerializedStyles
}

const TestimonialCard: React.FunctionComponent<TestimonialCardProps> = ({
  bgColor = 'blue',
  testimonialData = testimonials,
  cssStyles = css``,
}) => {
  const [currentTestimonial, setCurrentTestimonial] = React.useState(0)
  const { name, position, company, icon, link, image } = testimonialData[currentTestimonial]
  const { useTranslation } = createTranslation(testimonialData)
  const { t } = useTranslation()

  return (
    <div css={[containerStyle, cssStyles]}>
      {isMobile() ? (
        <div className={`${bgColor}-gradient-bg`} css={blockStyle}>
          <h3 className="heading3">"{parse(t(`${currentTestimonial}.quote`))}"</h3>
          <Block css={mediumSmallScreensBlockStyle}>
            {!!link ? (
              <Link className="opacity-on-hover" href={link} css={linkStyle}>
                <Image
                  className="fade-in-left"
                  src={image}
                  imgStyle={{ borderRadius: '50%' }}
                  alt={`${name}, ${position} - ${company}`}
                  css={imageStyle}
                />
              </Link>
            ) : (
              <Image className="fade-in-left" src={image} alt={`${name}, ${position} - ${company}`} css={imageStyle} />
            )}
            <div>
              <div className="body-1">
                {parse(name)}
                <span className="hide-on-small-medium">, </span> <br className="hide-on-large-desktop" />
                {t(`${currentTestimonial}.position`)} {isDesktop(breakpoints.small) && <span>- {parse(company)}</span>}
              </div>

              {!icon.includes('g2') &&
                !!icon &&
                (!!link ? (
                  <Link className="opacity-on-hover" href={link} css={linkStyle}>
                    <Image src={icon} alt={icon} css={iconImageStyle} />
                  </Link>
                ) : (
                  <Image src={icon} alt={icon} css={iconImageStyle} />
                ))}
            </div>
          </Block>
        </div>
      ) : (
        <Block
          className={`${bgColor}-gradient-bg`}
          columns={2}
          columnsTemplate="auto auto"
          noBreakForTablet
          columnGap={7}
          css={blockStyle}
        >
          {!!link ? (
            <Link className="opacity-on-hover" href={link} css={linkStyle}>
              <Image
                className="fade-in-left"
                src={image}
                imgStyle={{ borderRadius: '50%' }}
                alt={`${name}, ${position} - ${company}`}
                css={imageStyle}
              />
            </Link>
          ) : (
            <Image className="fade-in-left" src={image} alt={`${name}, ${position} - ${company}`} css={imageStyle} />
          )}
          <div css={contentStyle}>
            <h3 className="heading3">"{parse(t(`${currentTestimonial}.quote`))}"</h3>
            <div>
              <div className="body-1">
                {parse(name)}
                <span className="hide-on-small">, </span> <br className="hide-on-medium-large-desktop" />
                {t(`${currentTestimonial}.position`)} - {parse(company)}
              </div>

              {!!icon &&
                (!!link ? (
                  <Link className="opacity-on-hover" href={link} css={linkStyle}>
                    <Image src={icon} alt={icon} css={iconImageStyle} />
                  </Link>
                ) : (
                  <Image src={icon} alt={icon} css={iconImageStyle} />
                ))}
            </div>
          </div>
        </Block>
      )}
      {testimonialData.length > 1 && (
        <PaginationBlock
          circlesQuantity={testimonialData.length}
          currentSelection={currentTestimonial}
          setCurrentSelection={setCurrentTestimonial}
        />
      )}
    </div>
  )
}

export default TestimonialCard

const containerStyle = css`
  margin: 8rem 0;

  @media screen and (max-width: ${breakpoints.small}px) {
    margin: 4rem -1.5rem;
  }
`

const blockStyle = css`
  height: 21rem;
  padding: 4rem 6rem;
  box-sizing: border-box;
  border-radius: 0.5rem;
  box-shadow: rgb(0 0 0 / 8%) 0px 0.5rem 2rem;

  @media screen and (max-width: ${breakpoints.xlarge}px) {
    padding: 0 4rem;
    gap: 5rem;
  }

  @media screen and (max-width: ${breakpoints.large}px) {
    padding: 0 3rem;
    gap: 5%;
  }

  @media screen and (max-width: ${breakpoints.medium}px) {
    display: flex;
    flex-direction: column;
    justify-content: center;
    background-position-x: 85%;
    height: 22rem;
    grid-template-columns: 1fr 2fr;
    gap: 2rem;
  }

  @media screen and (max-width: ${breakpoints.small}px) {
    justify-content: space-evenly;
    padding: 2rem 2.5rem;
    height: auto;
    border-radius: 0;
    min-height: 30rem;
  }
`

const mediumSmallScreensBlockStyle = css`
  @media screen and (max-width: ${breakpoints.medium}px) {
    grid-template-columns: 1fr 4fr;
    column-gap: 1.5rem;

    .body-1 {
      margin-top: 0;
    }
  }
`

const linkStyle = css`
  border-radius: 50%;
`

const imageStyle = css`
  width: 13rem;
  height: 13rem;
  border-radius: 50%;
  box-sizing: border-box;
  border: 3px solid #fff;
  box-shadow: rgb(0 0 0 / 8%) 0px 0.5rem 2rem;

  @media screen and (max-width: ${breakpoints.medium}px) {
    width: 6rem;
    height: 6rem;
  }

  @media screen and (max-width: ${breakpoints.small}px) {
    aspect-ratio: 1;
    border: 2px solid #fff;
    margin: 0;
  }
`

const contentStyle = css`
  align-self: stretch;
  display: flex;
  flex-direction: column;
  justify-content: space-between;

  @media screen and (max-width: ${breakpoints.xlarge}px) {
    align-self: center;
  }

  @media screen and (max-width: ${breakpoints.small}px) {
    justify-content: flex-start;

    .body-1 {
      margin-top: 0.75rem;
    }
  }
`

const iconImageStyle = css`
  max-width: 90px;
`
